// src/components/users/UserList.js
import { useState, useEffect } from 'react';
import api from '../../services/api';
import Pagination from '../Pagination';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import useAuth from '../../hooks/useAuth';
import format from '../../utils/format';
import UserEditField from './UserEditField';
import RolesModal from './RolesModal';
import ProjectsModal from './ProjectsModal';
import UserForm from './UserForm';

const UserList = () => {
  const { user: currentUser, projects } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;
  const [userToDelete, setUserToDelete] = useState(null);
  const [selectedUserForPasswordChange, setSelectedUserForPasswordChange] = useState(null);
  const [passwordForm, setPasswordForm] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [currentUserInfo, setCurrentUserInfo] = useState(null);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedRolesUser, setSelectedRolesUser] = useState(null);
  const [selectedProjectsUser, setSelectedProjectsUser] = useState(null);


  useEffect(() => {
    fetchUsers();
  }, [page, search]);

  useEffect(() => {
    // При изменении поиска сбрасываем на первую страницу
    setPage(1);
  }, [search]);

  // Метод fetchAllProjects удален, так как проекты теперь берутся из useAuth

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/users', {
        params: { page, search, per_page: itemsPerPage },
      });
      
      // Ожидаем, что сервер возвращает объект с пагинацией
      if (res.data && res.data.users && res.data.pagination) {
        // Формируем список: сначала current_user, потом остальные
        const sortedUsers = [];
        
        // Добавляем current_user в начало, если он еще не в списке
        if (res.data.users && !res.data.users.some(u => u.id === currentUser.id)) {
          sortedUsers.push(currentUser);
        }
        
        // Добавляем пользователей с текущей страницы
        sortedUsers.push(...res.data.users);
        
        setUsers(sortedUsers);
        setTotalPages(res.data.pagination.pages || 1);
      } else {
        // Если сервер возвращает только массив, пытаемся определить totalPages
        if (Array.isArray(res.data)) {
          setUsers(res.data);
          // Если получили меньше пользователей, чем itemsPerPage, значит это последняя страница
          setTotalPages(res.data.length < itemsPerPage ? page : Math.max(page, 1));
        } else {
          setUsers([]);
          setTotalPages(1);
        }
      }
    } catch (err) {
      setError('Не удалось загрузить пользователей');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldSave = (user, field, value) => {
    setUsers(prev => prev.map(u => 
      u.id === user.id ? { ...u, [field]: value } : u
    ));
  };

  const handleRolesSave = (user, roles) => {
    setUsers(prev => prev.map(u => 
      u.id === user.id ? { ...u, roles } : u
    ));
  };

  const handleProjectsSave = (user, projectIds) => {
    const userProjects = projects.filter(p => projectIds.includes(p.id));
    setUsers(prev => prev.map(u =>
      u.id === user.id ? { ...u, projects: userProjects } : u
    ));
  };

  const handleGenerateToken = async (user) => {
    try {
      const res = await api.post(`/user/${user.id}/generate_token`);
      setTokenData(res.data);
      setShowTokenModal(true);
      // toast.success(res.data.existing ?
      //   'Возвращён существующий токен' :
      //   'Токен успешно создан');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка при создании токена');
    }
  };

  const handleDeactivateToken = async () => {
    if (!tokenData || !tokenData.token_id) return;
    
    try {
      const res = await api.post(`/token/${tokenData.token_id}/deactivate`);
      // toast.success(res.data.message);
      closeTokenModal();
      // Обновляем список пользователей, чтобы учесть изменения
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка при деактивации токена');
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await api.delete(`/user/${userToDelete.id}`);
      // toast.success('Пользователь успешно удалён');
      setUserToDelete(null);
      fetchUsers(); // Обновляем список пользователей
    } catch (err) {
      // toast.error(err.response?.data?.error || 'Ошибка при удалении пользователя');
      console.error('Ошибка при удалении пользователя:', err);
    }
  };

  const handlePasswordChange = async () => {
    if (!selectedUserForPasswordChange) return;
    
    // Проверка совпадения паролей
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('Новые пароли не совпадают');
      return;
    }
    
    // Проверка длины пароля
    if (passwordForm.new_password.length < 6) {
      setPasswordError('Пароль должен быть не менее 6 символов');
      return;
    }
    
    try {
      const payload = {
        new_password: passwordForm.new_password,
        confirm_password: passwordForm.confirm_password
      };
      
      // Если не superuser, добавляем старый пароль
      if (!currentUser.superuser) {
        payload.old_password = passwordForm.old_password;
      }
      
      const res = await api.post(`/user/${selectedUserForPasswordChange.id}/password`, payload);
      
      if (res.data.success) {
        // toast.success(res.data.message);
        setSelectedUserForPasswordChange(null);
        setPasswordForm({
          old_password: '',
          new_password: '',
          confirm_password: ''
        });
        setPasswordError('');
      }
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Ошибка при смене пароля');
    }
  };

  const closeTokenModal = () => {
    setShowTokenModal(false);
    setTokenData(null);
  };

  if (loading && users.length === 0) {
    return <LoadingSpinner text="Загрузка пользователей..." />;
  }

  return (
    <div className="card mt-4">
      <div className="card-header d-flex justify-content-between align-items-center">
      {selectedUserForPasswordChange && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Сменить пароль для {selectedUserForPasswordChange.username}</h5>
                <button type="button" className="btn-close" onClick={() => setSelectedUserForPasswordChange(null)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handlePasswordChange}>
                  {!currentUser.superuser && (
                    <div className="mb-3">
                      <label className="form-label">Старый пароль</label>
                      <input
                        type="password"
                        className="form-control"
                        value={passwordForm.old_password}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, old_password: e.target.value }))}
                        required={!currentUser.superuser}
                      />
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">Новый пароль</label>
                    <input
                      type="password"
                      className="form-control"
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Подтвердите новый пароль</label>
                    <input
                      type="password"
                      className="form-control"
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                      required
                    />
                  </div>
                  {passwordError && <div className="alert alert-danger">{passwordError}</div>}
                </form>
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={handlePasswordChange}>
                  Сменить пароль
                </button>
                <button className="btn btn-secondary" onClick={() => setSelectedUserForPasswordChange(null)}>
                  Отмена
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
        <h5>Пользователи</h5>
        <button
          className="btn btn-sm btn-primary"
          onClick={() => setEditingUser({})}
        >
          + Новый
        </button>
      </div>
      <div className="card-body">
        <ErrorMessage message={error} onClose={() => setError('')} />

        <div className="mb-3">
          <input
            type="text"
            className="form-control"
            placeholder="Поиск по логину, имени, фамилии..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <table className="table table-striped user-table">
          <thead>
            <tr>
              <th>Логин</th>
              <th>Имя</th>
              <th>Фамилия</th>
              <th>Email</th>
              <th>Полное имя</th>
              <th>ФИО в эра</th>
              <th>Проекты</th>
              <th>Роли</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center">
                  Нет пользователей по заданным параметрам
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id} className={user.id === currentUser.id ? 'table-primary' : ''}>
                  <td>
                    <UserEditField 
                      user={user} 
                      field="username" 
                      label="Логин" 
                      value={user.username} 
                      onSave={(field, value) => handleFieldSave(user, field, value)} 
                    />
                  </td>
                  <td>
                    <UserEditField 
                      user={user} 
                      field="name" 
                      label="Имя" 
                      value={user.name} 
                      onSave={(field, value) => handleFieldSave(user, field, value)} 
                    />
                  </td>
                  <td>
                    <UserEditField 
                      user={user} 
                      field="surname" 
                      label="Фамилия" 
                      value={user.surname} 
                      onSave={(field, value) => handleFieldSave(user, field, value)} 
                    />
                  </td>
                  <td>
                    <UserEditField 
                      user={user} 
                      field="email" 
                      label="Email" 
                      value={user.email || ''} 
                      onSave={(field, value) => handleFieldSave(user, field, value)} 
                    />
                  </td>
                  <td>{format.formatUserName(user)}</td>
                  <td>
                    <UserEditField 
                      user={user} 
                      field="era_name" 
                      label="ФИО в эра" 
                      value={user.era_name || ''} 
                      onSave={(field, value) => handleFieldSave(user, field, value)} 
                    />
                  </td>
                  <td>
                    <div className="user-actions d-flex flex-wrap align-items-center">
                      <span className="me-2 flex-grow-1">
                        {user.projects
                          .filter(project => projects.some(p => p.id === project.id))
                          .map(p => p.name)
                          .join(', ') || '—'}
                      </span>
                      <button 
                        className="edit-btn"
                        onClick={() => setSelectedProjectsUser(user)}
                        title="Редактировать проекты"
                      >
                        ✎
                      </button>
                    </div>
                  </td>
                  <td>
                    <div className="user-actions d-flex flex-wrap align-items-center">
                      <span className="me-2 flex-grow-1">
                        {user.roles.map(r => r.name).join(', ') || '—'}
                      </span>
                      <button 
                        className="edit-btn"
                        onClick={() => setSelectedRolesUser(user)}
                        title="Редактировать роли"
                      >
                        ✎
                      </button>
                    </div>
                  </td>
                  <td>
                    { (currentUser.superuser || currentUser.roles.some(role => role.name === 'admin')) && (
                      <div className="user-actions d-flex flex-wrap">
                        <button
                          className="btn btn-sm btn-warning me-2"
                          onClick={() => {
                            setSelectedUserForPasswordChange(user);
                            setPasswordForm({
                              old_password: '',
                              new_password: '',
                              confirm_password: ''
                            });
                            setPasswordError('');
                          }}
                          title="Сменить пароль"
                        >
                          🔒
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => setUserToDelete(user)}
                          title="Удалить пользователя"
                        >
                          🗑️
                        </button>
                      </div>
                    )}
                    { currentUser.superuser ? (
                      <div className="user-actions d-flex flex-wrap">
                        <button
                          className="btn btn-sm btn-info me-2"
                          onClick={() => handleGenerateToken(user)}
                          title="Создать JWT токен для пользователя"
                        >
                          🗝️
                        </button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
      </div>

      {editingUser && (
        <UserForm
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onUpdated={() => {
            setEditingUser(null);
            fetchUsers();
          }}
        />
      )}

      {selectedRolesUser && (
        <RolesModal 
          user={selectedRolesUser} 
          onClose={() => setSelectedRolesUser(null)} 
          onSave={(roles) => handleRolesSave(selectedRolesUser, roles)} 
        />
      )}

      {selectedProjectsUser && (
        <ProjectsModal 
          user={selectedProjectsUser} 
          allProjects={projects} 
          onClose={() => setSelectedProjectsUser(null)} 
          onSave={(projectIds) => handleProjectsSave(selectedProjectsUser, projectIds)} 
        />
      )}

      {showTokenModal && tokenData && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">JWT Токен для {tokenData.user?.username}</h5>
                <button type="button" className="btn-close" onClick={closeTokenModal}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Access Token:</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={tokenData.access_token}
                    readOnly
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label className="form-label">Refresh Token:</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={tokenData.refresh_token}
                    readOnly
                  ></textarea>
                </div>
                <div className="alert alert-info">
                  <small>Тип: {tokenData.token_type}, Срок действия: {tokenData.expires_in ? `${tokenData.expires_in} сек` : 'бессрочный'}</small>
                </div>
                {tokenData.existing && tokenData.token_id && (
                  <div className="alert alert-warning">
                    <small>Это существующий активный токен. Вы можете деактивировать его, если он больше не нужен.</small>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {tokenData.existing && tokenData.token_id && (
                  <button 
                    className="btn btn-danger me-2"
                    onClick={handleDeactivateToken}
                    title="Деактивировать этот токен"
                  >
                    Деактивировать токен
                  </button>
                )}
                <button className="btn btn-secondary" onClick={closeTokenModal}>Закрыть</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {userToDelete && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Подтверждение удаления</h5>
                <button type="button" className="btn-close" onClick={() => setUserToDelete(null)}></button>
              </div>
              <div className="modal-body">
                <p>Вы уверены, что хотите удалить пользователя <strong>{userToDelete.username}</strong>?</p>
                <p>Это действие нельзя отменить.</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-danger" onClick={handleDeleteUser}>
                  Удалить
                </button>
                <button className="btn btn-secondary" onClick={() => setUserToDelete(null)}>
                  Отмена
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;




